import React, { useEffect, useState } from "react";
import "./ObservationPreview.css";
import {
  HiOutlineXMark,
  HiOutlineCalendarDays,
  HiOutlineUser,
  HiOutlineBuildingOffice,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineClock,
  HiOutlineMapPin,
  HiOutlineDocumentText,
  HiOutlineUsers,
} from "react-icons/hi2";
import safetyObservationService from "../../../services/safetyObservationService";
import plantService from "../../../services/plantService";
import SharedModal from "../../../components/common/SharedModal";
import PageLoading from "../../../components/common/PageLoading";

const parseObserverString = (obsName, obsType) => {
  if (!obsName) return "-";

  const str = String(obsName);

  if (str.includes("COMP:") || str.includes("CONT:")) {
    return (
      <div className="observer-group-list">
        {str.split("|").map((part, idx) => {
          const [typePrefix, ...namesParts] = part.split(":");
          const names = namesParts.join(":");
          if (!names || !names.trim()) return null;

          const isComp = typePrefix === "COMP";
          return (
            <div key={idx} className="observer-group-item">
              <span
                className={`observer-group-title ${isComp ? "company" : "contractor"}`}
              >
                {isComp ? "COMPANY OBSERVER" : "CONTRACTOR OBSERVER"}
              </span>
              <div
                className="workforce-tags-container"
                style={{ marginTop: "0.25rem" }}
              >
                {names
                  .split(",")
                  .map((n) => n.trim())
                  .filter(Boolean)
                  .map((n, i) => (
                    <span
                      key={i}
                      className={`workforce-tag ${isComp ? "company" : "contractor"}`}
                      style={{ fontSize: "0.65rem", padding: "0.2rem 0.5rem" }}
                    >
                      {n}
                    </span>
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  return <span className="text-sm text-secondary font-medium">{obsName}</span>;
};

const ObservationPreview = ({ isOpen, onClose, roundId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [plants, setPlants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen && roundId) {
      loadInitialData();
    }
  }, [isOpen, roundId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError("");
      const [roundResult, plantsResult, categoriesResult] = await Promise.all([
        safetyObservationService.getSafetyObservationRoundById(roundId),
        plantService.getAllPlants().catch(() => []),
        safetyObservationService.getCategories().catch(() => []),
      ]);
      setData(roundResult);
      setPlants(plantsResult || []);
      setCategories(categoriesResult || []);
    } catch (err) {
      console.error("Failed to load preview data:", err);
      setError("Failed to resolve observation report details.");
    } finally {
      setLoading(false);
    }
  };

  const getPlantName = () => {
    if (data?.plantName && data.plantName !== "-") return data.plantName;
    const plant = plants.find((p) => (p.plantId || p.id) === data?.plantId);
    return plant?.plantName || "-";
  };

  const getCategoryName = (item) => {
    if (item.categoryName && item.categoryName !== "-")
      return item.categoryName;
    const catId = Number(item.categoryId || item.CategoryId);
    if (catId && categories.length > 0) {
      const cat = categories.find(
        (c) => Number(c.categoryId || c.CategoryId) === catId,
      );
      if (cat)
        return (cat.categoryName || cat.CategoryName || "-")
          .replace(/— SAFE|- SAFE|— UNSAFE|- UNSAFE/g, "")
          .trim();
    }
    return item.category || item.Category || "-";
  };

  const getSubCategoryName = (item) => {
    if (item.subCategoryName && item.subCategoryName !== "-")
      return item.subCategoryName;
    const subCatId = Number(item.subCategoryId || item.SubCategoryId);
    if (subCatId && categories.length > 0) {
      for (const cat of categories) {
        const subs = cat.subCategories || cat.SubCategories || [];
        const sub = subs.find(
          (s) => Number(s.subCategoryId || s.SubCategoryId) === subCatId,
        );
        if (sub) return sub.subCategoryName || sub.SubCategoryName || "-";
      }
    }
    return item.subCategory || item.SubCategory || "-";
  };

  const getInitials = (name) => {
    if (!name || name === "-" || name === "Assigned Member") return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .filter(Boolean)
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <SharedModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="preview-title">
          <h2>
            <HiOutlineDocumentText className="text-blue-600" size={24} />
            <span>
              {data?.roundName ||
                data?.RoundName ||
                "Safety Observation Report"}
            </span>
          </h2>
        </div>
      }
      customWidth="min(1100px, calc(100vw - 32px))"
      customClass="observation-preview-modal"
    >
      <div className="shared-modal-body preview-body-wrapper">
          {loading ? (
            <PageLoading variant="compact" message="Loading report…" />
          ) : error ? (
            <div className="report-message">
              <HiOutlineExclamationCircle className="text-red-500" size={48} />
              <p className="text-red-600 font-semibold">{error}</p>
            </div>
          ) : data ? (
            <div className="report-paper">
              {/* Report Header Banner */}
              <div className="report-header-banner">
                <div className="org-brand">
                  <span className="report-type">
                    Safety Behavior Observation
                  </span>
                  <span className="org-name">Plant Maintenance System</span>
                </div>
                <div className="report-id">
                  <span className="report-id-label">Record No:</span>
                  <div className="report-id-value">
                    {data.observationNumber || "N/A"}
                  </div>
                </div>
              </div>

              {/* General Information Section */}
              <div className="report-section">
                <div className="section-label">Report Metadata</div>
                <div className="metadata-container">
                  <div className="metadata-item">
                    <div className="metadata-icon icon-blue">
                      <HiOutlineDocumentText />
                    </div>
                    <div className="metadata-content">
                      <span className="metadata-label">Team Name</span>
                      <span className="metadata-value highlight-value">
                        {data.roundName || data.RoundName || "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className="metadata-item">
                    <div className="metadata-icon icon-indigo">
                      <HiOutlineClock />
                    </div>
                    <div className="metadata-content">
                      <span className="metadata-label">Date & Time</span>
                      <span className="metadata-value">
                        {data.observationDate
                          ? new Date(data.observationDate).toLocaleDateString()
                          : "-"}
                        <span className="separator">•</span>
                        {data.observationTime || "-"}
                      </span>
                    </div>
                  </div>

                  <div className="metadata-item">
                    <div className="metadata-icon icon-emerald">
                      <HiOutlineBuildingOffice />
                    </div>
                    <div className="metadata-content">
                      <span className="metadata-label">Primary Plant</span>
                      <span className="metadata-value">{getPlantName()}</span>
                    </div>
                  </div>

                  <div className="metadata-item">
                    <div className="metadata-icon icon-amber">
                      <HiOutlineMapPin />
                    </div>
                    <div className="metadata-content">
                      <span className="metadata-label">Location / Area</span>
                      <span className="metadata-value">
                        {data.departmentName || data.DepartmentName || "-"}{" "}
                        {data.area || data.Area
                          ? ` / ${data.area || data.Area}`
                          : ""}
                      </span>
                    </div>
                  </div>

                  <div className="metadata-item">
                    <div className="metadata-icon icon-purple">
                      <HiOutlineUser />
                    </div>
                    <div className="metadata-content">
                      <span className="metadata-label">Lead Observer</span>
                      <span className="metadata-value">
                        {data.employeeName ||
                          data.EmployeeName ||
                          data.FullName ||
                          data.fullName ||
                          data.createdBy ||
                          "Unassigned"}
                      </span>
                    </div>
                  </div>

                  <div className="metadata-item">
                    <div className="metadata-icon icon-slate">
                      <HiOutlineCheckCircle />
                    </div>
                    <div className="metadata-content">
                      <span className="metadata-label">Report Status</span>
                      <span className="metadata-value flex items-center h-full">
                        <span className={`badge badge-status`}>
                          {data.statusName ||
                            data.StatusName ||
                            data.status ||
                            data.Status ||
                            "Active"}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {data.description && (
                  <div className="metadata-context-box">
                    <span className="context-label">Context / Background</span>
                    <p className="context-value">{data.description}</p>
                  </div>
                )}
              </div>

              {/* Observation Metrics */}
              <div className="report-section">
                <div className="section-label">Observation Metrics</div>
                <div className="score-summary-grid">
                  <div className="score-card neutral">
                    <span className="label">Total Workforce</span>
                    <span className="value">
                      {(data.companyEmployeeCount || 0) +
                        (data.contractorEmployeeCount || 0)}
                    </span>
                    <span className="text-[10px] text-tertiary">
                      PEOPLE CONTACTED
                    </span>
                  </div>
                  <div className="score-card safe">
                    <span className="label">Safe Behaviors</span>
                    <span className="value">
                      {data.items?.filter(
                        (i) =>
                          i.observationType?.includes("Safe") &&
                          !i.observationType?.includes("Unsafe"),
                      ).length || 0}
                    </span>
                    <span className="text-[10px] text-tertiary text-green-600">
                      POSITIVE ACTS
                    </span>
                  </div>
                  <div className="score-card unsafe">
                    <span className="label">Unsafe Acts</span>
                    <span className="value">
                      {data.items?.filter((i) =>
                        i.observationType?.includes("Unsafe"),
                      ).length || 0}
                    </span>
                    <span className="text-[10px] text-tertiary text-red-500">
                      CORRECTIVE ACTIONS
                    </span>
                  </div>
                </div>

                {data.observers && data.observers.length > 0 && (
                  <div className="field-group mt-6">
                    <span className="field-label flex items-center gap-2">
                      <HiOutlineUsers className="text-blue-500" /> Observation
                      Team
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
                      {data.observers.map((obs, idx) => {
                        const rawName =
                          obs.observerName ||
                          obs.ObserverName ||
                          obs.name ||
                          obs.Name ||
                          obs.fullName ||
                          obs.FullName;
                        const name =
                          rawName && rawName !== "-"
                            ? rawName
                            : "Unknown Observer";
                        const role =
                          obs.observerRole || obs.ObserverRole || "Member";

                        return (
                          <div key={idx} className="observer-card group">
                            <div className="observer-avatar group-hover:scale-110 transition-transform">
                              {getInitials(name)}
                            </div>
                            <div className="observer-info">
                              <span className="observer-role">{role}</span>
                              <span className="observer-name">{name}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* People Contacted Details */}
              {(data.companyEmployeeNames?.length > 0 ||
                data.contractorEmployeeNames?.length > 0 ||
                data.CompanyEmployeeNames?.length > 0 ||
                data.ContractorEmployeeNames?.length > 0) && (
                <div className="report-section">
                  <div className="section-label">Workforce Contacted</div>
                  <div className="workforce-grid-wrapper">
                    {(data.companyEmployeeNames?.length > 0 ||
                      data.CompanyEmployeeNames?.length > 0) && (
                      <div className="field-group flex-1">
                        <span className="workforce-group-label company-label">
                          Company Personnel
                        </span>
                        <div className="workforce-tags-container">
                          {(
                            data.companyEmployeeNames ||
                            data.CompanyEmployeeNames
                          ).map((name, i) => (
                            <span key={i} className="workforce-tag company">
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(data.contractorEmployeeNames?.length > 0 ||
                      data.ContractorEmployeeNames?.length > 0) && (
                      <div className="field-group flex-1">
                        <span className="workforce-group-label contractor-label">
                          Contractor Personnel
                        </span>
                        <div className="workforce-tags-container">
                          {(
                            data.contractorEmployeeNames ||
                            data.ContractorEmployeeNames
                          ).map((name, i) => (
                            <span key={i} className="workforce-tag contractor">
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Safe Acts Table */}
              {data.items?.filter(
                (i) =>
                  i.observationType?.includes("Safe") &&
                  !i.observationType?.includes("Unsafe"),
              ).length > 0 && (
                <div className="report-section">
                  <div className="section-label safe-section-label">
                    Safe acts recorded
                  </div>
                  <div className="modern-table-wrapper safe-table-wrapper">
                    <table className="modern-table">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Sub-Category</th>
                          <th className="w-1/3">Positive Observation</th>
                          <th>Attachments</th>
                          <th>By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.items
                          .filter(
                            (i) =>
                              i.observationType?.includes("Safe") &&
                              !i.observationType?.includes("Unsafe"),
                          )
                          .map((item, idx) => (
                            <tr key={idx}>
                              <td>
                                <div className="font-bold text-emerald-700">
                                  {getCategoryName(item)}
                                </div>
                              </td>
                              <td className="text-sm font-medium text-secondary">
                                {getSubCategoryName(item)}
                              </td>
                              <td className="text-sm italic text-secondary">
                                {item.description || "-"}
                              </td>
                              <td className="align-middle">
                                {item.attachments?.length > 0 ? (
                                  <div className="flex flex-col gap-1.5 mt-1">
                                    {item.attachments.map((at, aIdx) => (
                                      <a
                                        key={aIdx}
                                        href={
                                          at.fileUrl ||
                                          at.url ||
                                          at.FileUrl ||
                                          "#"
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-700 bg-emerald-50/70 hover:bg-emerald-100 px-2 py-1 rounded border border-emerald-100 transition-all w-max !no-underline shadow-sm"
                                        title={
                                          at.fileName ||
                                          at.name ||
                                          at.FileName ||
                                          "Attachment"
                                        }
                                      >
                                        <HiOutlineDocumentText
                                          size={14}
                                          className="text-emerald-500 flex-shrink-0"
                                        />
                                        <span className="truncate max-w-[120px]">
                                          {at.fileName ||
                                            at.name ||
                                            at.FileName ||
                                            `File ${aIdx + 1}`}
                                        </span>
                                      </a>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-[11px] text-gray-400 font-medium italic">
                                    No files
                                  </span>
                                )}
                              </td>
                              <td className="align-top">
                                {parseObserverString(item.observerName)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Unsafe Acts Table */}
              {data.items?.filter((i) => i.observationType?.includes("Unsafe"))
                .length > 0 && (
                <div className="report-section">
                  <div className="section-label unsafe-section-label">
                    Unsafe acts & observations
                  </div>
                  <div className="modern-table-wrapper unsafe-table-wrapper">
                    <table className="modern-table">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Sub-Category</th>
                          <th>Details</th>
                          <th>Severity</th>
                          <th>Action Taken</th>
                          <th>Attachments</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.items
                          .filter((i) => i.observationType?.includes("Unsafe"))
                          .map((item, idx) => (
                            <tr key={idx}>
                              <td className="align-top">
                                <div className="font-bold text-red-700">
                                  {getCategoryName(item)}
                                </div>
                              </td>
                              <td className="align-top">
                                <div className="text-[11px] font-semibold text-tertiary uppercase tracking-wider">
                                  {getSubCategoryName(item)}
                                </div>
                              </td>
                              <td className="text-sm text-secondary">
                                {item.description || "-"}
                              </td>
                              <td>
                                <span
                                  className={`badge ${item.riskLevel === "Major" || item.riskLevel === "Critical" ? "badge-critical" : "badge-warning"}`}
                                >
                                  {item.riskLevel || "-"}
                                </span>
                              </td>
                              <td className="text-sm italic text-secondary">
                                {item.actionDescription || "-"}
                              </td>
                              <td className="align-middle">
                                {item.attachments?.length > 0 ? (
                                  <div className="flex flex-col gap-1.5 mt-1">
                                    {item.attachments.map((at, aIdx) => (
                                      <a
                                        key={aIdx}
                                        href={
                                          at.fileUrl ||
                                          at.url ||
                                          at.FileUrl ||
                                          "#"
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-[10px] font-bold text-red-700 bg-red-50/70 hover:bg-red-100 px-2 py-1 rounded border border-red-100 transition-all w-max !no-underline shadow-sm"
                                        title={
                                          at.fileName ||
                                          at.name ||
                                          at.FileName ||
                                          "Attachment"
                                        }
                                      >
                                        <HiOutlineDocumentText
                                          size={14}
                                          className="text-red-500 flex-shrink-0"
                                        />
                                        <span className="truncate max-w-[120px]">
                                          {at.fileName ||
                                            at.name ||
                                            at.FileName ||
                                            `File ${aIdx + 1}`}
                                        </span>
                                      </a>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-[11px] text-gray-400 font-medium italic">
                                    No files
                                  </span>
                                )}
                              </td>
                              <td>
                                <span
                                  className={`badge ${item.itemStatus === "Closed" ? "badge-safe" : "badge-unsafe"}`}
                                >
                                  {item.itemStatus || "Open"}
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Footer / Feedback */}
              {data.feedbackToPerson && (
                <div className="report-section">
                  <div className="section-label">Observer Feedback</div>
                  <div className="bg-page-bg p-6 rounded-xl border border-dashed border-gray-300">
                    <p className="text-secondary leading-relaxed font-medium">
                      "{data.feedbackToPerson}"
                    </p>
                  </div>
                </div>
              )}

              {/* Professional Footer Signature */}
              <div className="preview-footer-signature">
                <span>PMS Safety System © 2026</span>
                <span>Confidential Safety Document</span>
                <span>Page 01 of 01</span>
              </div>
            </div>
          ) : (
            <div className="report-message">No Data Available</div>
          )}
        </div>
    </SharedModal>
  );
};

export default ObservationPreview;
